/*
 * Nybroe order rules engine.
 * Source: plan/catering-flow-plan-v1.0.0.md section 3.2, with the section 0 corrections (C08 kill switch in the
 * nybroe_rules metaobject, C09 config fetched from sections/nyb-order-config.liquid, C14 guarded exports).
 *
 * Pure functions only: no DOM, no fetch, no storage. The caller passes the config object printed by
 * sections/nyb-order-config.liquid, the cart attributes and the current time in epoch milliseconds.
 * Runs as a classic deferred script (window.NybRules) and under `node --test` (module.exports).
 *
 * Time: every wall-clock value is Europe/Stockholm. Calendar maths works on plain YYYY-MM-DD strings at
 * UTC noon, so daylight-saving changes never shift a day.
 *
 * Fail closed: a missing config, a missing or duplicated rules entry, no delivery hall (or more than one),
 * a malformed HH:MM value or zone entry, or ordering_paused makes every date unselectable.
 */
(function () {
  'use strict';

  var TZ = 'Europe/Stockholm';

  var PICKUP = 'Avhämtning';
  var DELIVERY = 'Leverans';
  var HALL_BOTH = 'Båda hallarna';
  var APPLIES_BOTH = 'Avhämtning och leverans';
  var APPLIES_PICKUP = 'Endast avhämtning';
  var APPLIES_DELIVERY = 'Endast leverans';
  var ZONE_INSIDE_LABEL = 'Innanför tullarna';
  var ZONE_OUTSIDE_LABEL = 'Utanför tullarna';

  /* Cart attribute contract, plan 3.1. */
  var ATTRIBUTE_KEYS = [
    'Metod', 'Hall', 'Telefon', 'Postnummer', 'Zon', 'Datum', 'Veckodag', 'Tid',
    'Stopptid', 'StopptidEpoch', 'Validerad', 'Tidskalla', 'Regelversion'
  ];
  var DATE_KEYS = ['Datum', 'Veckodag', 'Tid', 'Stopptid', 'StopptidEpoch'];

  var WEEKDAY_LONG = ['söndag', 'måndag', 'tisdag', 'onsdag', 'torsdag', 'fredag', 'lördag'];
  var WEEKDAY_SHORT = ['sön', 'mån', 'tis', 'ons', 'tor', 'fre', 'lör'];
  var MONTH_LONG = ['januari', 'februari', 'mars', 'april', 'maj', 'juni', 'juli', 'augusti', 'september',
    'oktober', 'november', 'december'];
  var MONTH_SHORT = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
  /* Index = JavaScript getUTCDay() (0 = Sunday). */
  var HOUR_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  /* nybroe_rules.delivery_weekdays preset choices. Lör and Sön are deliberately not accepted. */
  var DELIVERY_DAY_CODES = { 'Mån': 1, 'Tis': 2, 'Ons': 3, 'Tor': 4, 'Fre': 5 };

  var DEFAULT_PAUSED_MESSAGE = 'Onlinebeställning är tillfälligt stängd. Kontakta oss.';
  var MAX_DAYS_BACK_FOR_CUTOFF = 31;
  var CLOSED_DATE_WARNING_THRESHOLD = 200;

  var RE_HM = /^([01]\d|2[0-3]):([0-5]\d)$/;
  var RE_RANGE = /^([01]\d|2[0-3]):([0-5]\d)-([01]\d|2[0-3]):([0-5]\d)$/;
  var RE_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
  var RE_ISO_WITH_OFFSET = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,3})?)?(Z|[+-]\d{2}:\d{2})$/;
  var RE_ZONE_EXACT = /^\d{5}$/;
  var RE_ZONE_PREFIX = /^(\d{1,4})\*$/;

  /* ------------------------------------------------------------------ small helpers */

  function str(v) {
    if (v === null || v === undefined) return '';
    return String(v).trim();
  }

  function pad2(n) {
    return (n < 10 ? '0' : '') + n;
  }

  function parseHM(s) {
    var m = RE_HM.exec(str(s));
    return m ? Number(m[1]) * 60 + Number(m[2]) : null;
  }

  /* "HH:MM-HH:MM" to {open, close} in minutes after midnight; null when malformed or close <= open. */
  function parseRange(s) {
    var m = RE_RANGE.exec(str(s));
    if (!m) return null;
    var open = Number(m[1]) * 60 + Number(m[2]);
    var close = Number(m[3]) * 60 + Number(m[4]);
    if (close <= open) return null;
    return { open: open, close: close };
  }

  function formatHM(minutes) {
    return pad2(Math.floor(minutes / 60)) + ':' + pad2(minutes % 60);
  }

  function formatRange(r) {
    return formatHM(r.open) + '-' + formatHM(r.close);
  }

  function isDateString(s) {
    var m = RE_DATE.exec(str(s));
    if (!m) return false;
    var d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12));
    return d.getUTCFullYear() === Number(m[1]) && d.getUTCMonth() === Number(m[2]) - 1 &&
      d.getUTCDate() === Number(m[3]);
  }

  function noonUtc(dateStr) {
    return Date.parse(dateStr + 'T12:00:00Z');
  }

  function addDays(dateStr, n) {
    var d = new Date(noonUtc(dateStr));
    d.setUTCDate(d.getUTCDate() + n);
    return d.toISOString().slice(0, 10);
  }

  function daysBetween(fromDate, toDate) {
    return Math.round((noonUtc(toDate) - noonUtc(fromDate)) / 86400000);
  }

  /* 0 = Sunday ... 6 = Saturday */
  function weekdayOf(dateStr) {
    return new Date(noonUtc(dateStr)).getUTCDay();
  }

  function weekdayName(dateStr) {
    return WEEKDAY_LONG[weekdayOf(dateStr)];
  }

  /* "fre 18 sep" */
  function formatDateShort(dateStr) {
    var d = new Date(noonUtc(dateStr));
    return WEEKDAY_SHORT[d.getUTCDay()] + ' ' + d.getUTCDate() + ' ' + MONTH_SHORT[d.getUTCMonth()];
  }

  /* "fredag 18 september" */
  function formatDateLong(dateStr) {
    var d = new Date(noonUtc(dateStr));
    return WEEKDAY_LONG[d.getUTCDay()] + ' ' + d.getUTCDate() + ' ' + MONTH_LONG[d.getUTCMonth()];
  }

  /* "18 september" */
  function formatDayMonth(dateStr) {
    var d = new Date(noonUtc(dateStr));
    return d.getUTCDate() + ' ' + MONTH_LONG[d.getUTCMonth()];
  }

  /* ------------------------------------------------------------------ Europe/Stockholm clock */

  var formatterCache = null;

  function stockholmFormatter() {
    if (!formatterCache) {
      formatterCache = new Intl.DateTimeFormat('en-CA', {
        timeZone: TZ,
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    }
    return formatterCache;
  }

  /* Wall clock in Stockholm for an epoch in milliseconds. */
  function stockholmParts(ms) {
    var parts = {};
    stockholmFormatter().formatToParts(new Date(ms)).forEach(function (p) {
      parts[p.type] = p.value;
    });
    var hour = Number(parts.hour);
    if (hour === 24) hour = 0;
    var minute = Number(parts.minute);
    return {
      date: parts.year + '-' + parts.month + '-' + parts.day,
      hour: hour,
      minute: minute,
      second: Number(parts.second),
      minutes: hour * 60 + minute
    };
  }

  /* Stockholm UTC offset in minutes at an instant (+120 in summer, +60 in winter). */
  function stockholmOffsetMinutes(ms) {
    var whole = Math.floor(ms / 1000) * 1000;
    var p = stockholmParts(whole);
    var asUtc = Date.UTC(Number(p.date.slice(0, 4)), Number(p.date.slice(5, 7)) - 1, Number(p.date.slice(8, 10)),
      p.hour, p.minute, p.second);
    return Math.round((asUtc - whole) / 60000);
  }

  /* Epoch milliseconds of a Stockholm wall-clock time on a date. */
  function stockholmEpochMs(dateStr, minutesAfterMidnight) {
    var naive = Date.UTC(Number(dateStr.slice(0, 4)), Number(dateStr.slice(5, 7)) - 1, Number(dateStr.slice(8, 10)),
      0, minutesAfterMidnight);
    var off1 = stockholmOffsetMinutes(naive);
    var t = naive - off1 * 60000;
    var off2 = stockholmOffsetMinutes(t);
    if (off2 !== off1) t = naive - off2 * 60000;
    return t;
  }

  /* "2026-09-17T13:58:41+02:00" */
  function formatStockholmIso(ms) {
    var p = stockholmParts(ms);
    var off = stockholmOffsetMinutes(ms);
    var sign = off < 0 ? '-' : '+';
    var abs = Math.abs(off);
    return p.date + 'T' + pad2(p.hour) + ':' + pad2(p.minute) + ':' + pad2(p.second) + sign +
      pad2(Math.floor(abs / 60)) + ':' + pad2(abs % 60);
  }

  /*
   * Which clock to trust. options:
   *   dateHeader   the `Date` response header of /cart.js (server time)
   *   deviceNowMs  device time, used only when the header is missing or unreadable
   *   testClock    config.test_clock (theme setting nyb_test_clock)
   *   nybNow       the ?nyb_now= value (ISO 8601 with an offset); honoured only when testClock is true
   * Returns {ms, source} with source 'server', 'enhet' (device) or 'test'.
   */
  function resolveNow(options) {
    var o = options || {};
    if (o.testClock === true && typeof o.nybNow === 'string' && RE_ISO_WITH_OFFSET.test(o.nybNow)) {
      var t = Date.parse(o.nybNow);
      if (!isNaN(t)) return { ms: t, source: 'test' };
    }
    if (o.dateHeader) {
      var s = Date.parse(o.dateHeader);
      if (!isNaN(s)) return { ms: s, source: 'server' };
    }
    var device = typeof o.deviceNowMs === 'number' && !isNaN(o.deviceNowMs) ? o.deviceNowMs : Date.now();
    return { ms: device, source: 'enhet' };
  }

  /* ------------------------------------------------------------------ postcodes (mirror Shopify zone format) */

  /*
   * Shopify local delivery postal codes: entries separated by comma and space, each a full code or a prefix
   * ending in `*` [S01]. Nybroe codes are Swedish: 5 digits, or 1 to 4 digits plus `*`. Anything else is a
   * data error (the admin string and the metaobject string must be byte-identical and contain no spaces, C27).
   */
  function parseZoneString(s) {
    var entries = [];
    var invalid = [];
    String(s === null || s === undefined ? '' : s).split(',').forEach(function (raw) {
      var e = raw.trim();
      if (e === '') return;
      var prefix = RE_ZONE_PREFIX.exec(e);
      if (RE_ZONE_EXACT.test(e)) entries.push({ type: 'exact', code: e });
      else if (prefix) entries.push({ type: 'prefix', code: prefix[1] });
      else invalid.push(e);
    });
    return { entries: entries, invalid: invalid };
  }

  /* Customer input: remove whitespace (including no-break spaces); the result must be exactly 5 digits. */
  function normalizePostcode(input) {
    var s = String(input === null || input === undefined ? '' : input).replace(/\s+/g, '');
    return RE_ZONE_EXACT.test(s) ? s : null;
  }

  function zoneMatches(postcode, entries) {
    for (var i = 0; i < entries.length; i++) {
      var e = entries[i];
      if (e.type === 'exact' ? postcode === e.code : postcode.indexOf(e.code) === 0) return true;
    }
    return false;
  }

  /* Inside is tested before outside; null when the postcode is malformed or not served. */
  function zoneFor(input, config) {
    if (!config || !config.zones) return null;
    var pc = normalizePostcode(input);
    if (!pc) return null;
    if (zoneMatches(pc, config.zones.inside.entries)) {
      return { zone: 'inside', label: ZONE_INSIDE_LABEL, price: config.zones.inside.price, postcode: pc };
    }
    if (zoneMatches(pc, config.zones.outside.entries)) {
      return { zone: 'outside', label: ZONE_OUTSIDE_LABEL, price: config.zones.outside.price, postcode: pc };
    }
    return null;
  }

  /* After removing spaces, hyphens and a leading `+`: 8 to 15 digits. */
  function phoneOk(value) {
    var s = String(value === null || value === undefined ? '' : value).replace(/[\s-]/g, '');
    if (s.charAt(0) === '+') s = s.slice(1);
    return /^\d{8,15}$/.test(s);
  }

  /* ------------------------------------------------------------------ config */

  /* Extract the JSON printed by sections/nyb-order-config.liquid from a Section Rendering API response. */
  function configFromSectionsResponse(json) {
    if (!json || typeof json !== 'object') return null;
    var html = json['nyb-order-config'];
    if (typeof html !== 'string') return null;
    var m = /<script[^>]*\bid="nyb-order-config"[^>]*>([\s\S]*?)<\/script>/.exec(html);
    if (!m) return null;
    try {
      return JSON.parse(m[1]);
    } catch (e) {
      return null;
    }
  }

  /*
   * Normalise the raw config object. Returns
   *   {ok, errors[], warnings[], testClock, rules, halls[], deliveryHall, closedDates[], closedByDate{}, zones}
   * ok is false when anything that decides a date, a time or a price is missing or malformed.
   */
  function normalizeConfig(raw) {
    var errors = [];
    var warnings = [];
    function err(code, field, value) {
      var e = { code: code };
      if (field !== undefined) e.field = field;
      if (value !== undefined) e.value = value;
      errors.push(e);
    }

    var cfg = {
      ok: false,
      errors: errors,
      warnings: warnings,
      testClock: false,
      rules: null,
      halls: [],
      deliveryHall: null,
      closedDates: [],
      closedByDate: {},
      zones: null
    };

    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
      err('config_missing');
      return cfg;
    }

    cfg.testClock = raw.test_clock === true;

    if (Array.isArray(raw.errors)) {
      raw.errors.forEach(function (code) {
        err('liquid', String(code));
      });
    }

    /* ---- rules */
    var r = raw.rules;
    if (!r || typeof r !== 'object') {
      if (!(Array.isArray(raw.errors) && raw.errors.length)) err('rules_missing');
    } else {
      var rules = {};

      var readBool = function (key, def) {
        var v = r[key];
        if (v === null || v === undefined || v === '') return def;
        if (v === true || v === false) return v;
        err('rules_field', key, v);
        return def;
      };
      var readInt = function (key, def, min, max) {
        var v = r[key];
        if (v === null || v === undefined || v === '') return def;
        var n = typeof v === 'number' ? v : (/^-?\d+$/.test(String(v)) ? Number(v) : NaN);
        if (!(Math.floor(n) === n) || n < min || n > max) {
          err('rules_field', key, v);
          return def;
        }
        return n;
      };

      rules.ordering_paused = readBool('ordering_paused', false);
      rules.paused_message = str(r.paused_message) || DEFAULT_PAUSED_MESSAGE;

      rules.cutoff_time = str(r.cutoff_time);
      rules.cutoff_minutes = parseHM(rules.cutoff_time);
      if (rules.cutoff_minutes === null) err('rules_field', 'cutoff_time', r.cutoff_time === undefined ? null : r.cutoff_time);

      rules.saturday_is_order_day = readBool('saturday_is_order_day', false);
      rules.closed_day_is_order_day = readBool('closed_day_is_order_day', false);
      rules.horizon_days = readInt('horizon_days', 60, 1, 366);
      rules.pickup_slot_minutes = readInt('pickup_slot_minutes', 30, 5, 240);
      rules.pickup_last_slot_before_close_minutes = readInt('pickup_last_slot_before_close_minutes', 30, 0, 240);
      rules.pickup_phone_required = readBool('pickup_phone_required', true);

      /* An unset list means "none": no delivery days or windows, so delivery fails closed. */
      rules.delivery_weekdays = [];
      rules.delivery_days = {};
      if (r.delivery_weekdays !== null && r.delivery_weekdays !== undefined) {
        if (!Array.isArray(r.delivery_weekdays)) {
          err('rules_field', 'delivery_weekdays', r.delivery_weekdays);
        } else {
          r.delivery_weekdays.forEach(function (code) {
            var c = str(code);
            if (Object.prototype.hasOwnProperty.call(DELIVERY_DAY_CODES, c)) {
              rules.delivery_weekdays.push(c);
              rules.delivery_days[DELIVERY_DAY_CODES[c]] = true;
            } else {
              err('rules_field', 'delivery_weekdays', code);
            }
          });
        }
      }

      rules.delivery_windows = [];
      if (r.delivery_windows !== null && r.delivery_windows !== undefined) {
        if (!Array.isArray(r.delivery_windows)) {
          err('rules_field', 'delivery_windows', r.delivery_windows);
        } else {
          r.delivery_windows.forEach(function (w) {
            var range = parseRange(w);
            if (range) rules.delivery_windows.push(range);
            else err('rules_field', 'delivery_windows', w);
          });
          rules.delivery_windows.sort(function (a, b) {
            return a.open - b.open || a.close - b.close;
          });
        }
      }

      var inside = parseZoneString(r.zone_inside_postcodes);
      var outside = parseZoneString(r.zone_outside_postcodes);
      inside.invalid.forEach(function (e) { err('zone_entry', 'zone_inside_postcodes', e); });
      outside.invalid.forEach(function (e) { err('zone_entry', 'zone_outside_postcodes', e); });
      cfg.zones = {
        inside: { entries: inside.entries, price: readInt('zone_inside_price', null, 0, 100000) },
        outside: { entries: outside.entries, price: readInt('zone_outside_price', null, 0, 100000) }
      };

      rules.contact_email = str(r.contact_email);
      rules.contact_phone = str(r.contact_phone);
      rules.pay_in_store_name = str(r.pay_in_store_name);
      rules.rules_version = str(r.rules_version);
      cfg.rules = rules;
    }

    /* ---- halls */
    var hallNames = {};
    var hallLocations = {};
    if (!Array.isArray(raw.halls) || raw.halls.length === 0) {
      if (!(Array.isArray(raw.errors) && raw.errors.length)) err('halls_missing');
    } else {
      raw.halls.forEach(function (h, i) {
        h = h || {};
        var name = str(h.name);
        var loc = str(h.location_name);
        if (!name) err('hall_field', 'halls[' + i + '].name');
        else if (hallNames[name]) err('hall_field', 'halls[' + i + '].name', name);
        if (!loc) err('hall_field', 'halls[' + i + '].location_name');
        else if (hallLocations[loc]) err('hall_field', 'halls[' + i + '].location_name', loc);

        var hours = {};
        HOUR_KEYS.forEach(function (k) {
          var v = str(h.hours ? h.hours[k] : '');
          if (v === '') {
            hours[k] = null;
          } else {
            var range = parseRange(v);
            if (range) hours[k] = range;
            else {
              hours[k] = null;
              err('hall_field', (name || 'halls[' + i + ']') + '.hours_' + k, v);
            }
          }
        });

        var hall = {
          name: name,
          location_name: loc,
          pickup_enabled: h.pickup_enabled === true,
          delivery_hall: h.delivery_hall === true,
          hours: hours,
          address: str(h.address),
          pickup_where: str(h.pickup_where),
          color: str(h.color),
          text_color: str(h.text_color),
          border_style: str(h.border_style)
        };
        if (name) hallNames[name] = hall;
        if (loc) hallLocations[loc] = hall;
        cfg.halls.push(hall);
      });

      var deliveryHalls = cfg.halls.filter(function (h) { return h.delivery_hall; });
      if (deliveryHalls.length !== 1) err('delivery_hall_count', 'delivery_hall', deliveryHalls.length);
      else cfg.deliveryHall = deliveryHalls[0];
    }

    /* ---- closed dates (a malformed entry is ignored and reported, never fatal) */
    if (Array.isArray(raw.closed_dates)) {
      raw.closed_dates.forEach(function (c, i) {
        c = c || {};
        var date = str(c.date);
        var hall = str(c.hall) || HALL_BOTH;
        var applies = str(c.applies_to) || APPLIES_BOTH;
        var openHours = str(c.open_hours);
        var bad = [];
        if (!isDateString(date)) bad.push('date');
        if (hall !== HALL_BOTH && !hallNames[hall]) bad.push('hall');
        if ([APPLIES_BOTH, APPLIES_PICKUP, APPLIES_DELIVERY].indexOf(applies) < 0) bad.push('applies_to');
        var range = null;
        if (openHours !== '') {
          range = parseRange(openHours);
          if (!range) bad.push('open_hours');
        }
        if (bad.length) {
          warnings.push({ code: 'closed_date_ignored', index: i, date: date, fields: bad });
          return;
        }
        var entry = { date: date, hall: hall, applies_to: applies, hours: range, message: str(c.customer_message), index: i };
        cfg.closedDates.push(entry);
        (cfg.closedByDate[date] = cfg.closedByDate[date] || []).push(entry);
      });
    }
    var closedCount = typeof raw.closed_dates_count === 'number' ? raw.closed_dates_count : cfg.closedDates.length;
    if (closedCount >= CLOSED_DATE_WARNING_THRESHOLD) warnings.push({ code: 'closed_dates_many', count: closedCount });

    cfg.ok = errors.length === 0 && cfg.rules !== null && cfg.deliveryHall !== null;
    return cfg;
  }

  function loadConfigFromSectionsResponse(json) {
    return normalizeConfig(configFromSectionsResponse(json));
  }

  /* ------------------------------------------------------------------ rules */

  function hallByLocation(config, locationName) {
    var loc = str(locationName);
    for (var i = 0; i < config.halls.length; i++) {
      if (config.halls[i].location_name === loc) return config.halls[i];
    }
    return null;
  }

  /* The hall an order uses: a pickup-enabled hall by location name, or the delivery hall. */
  function resolveHall(config, method, locationName) {
    if (method === PICKUP) {
      var h = hallByLocation(config, locationName);
      return h && h.pickup_enabled ? h : null;
    }
    if (method === DELIVERY) {
      var d = config.deliveryHall;
      if (!d) return null;
      if (str(locationName) !== '' && str(locationName) !== d.location_name) return null;
      return d;
    }
    return null;
  }

  function closedEntriesFor(config, dateStr) {
    return config.closedByDate[dateStr] || [];
  }

  /* Closed at both halls for both methods all day: the only kind of entry that can move a cut-off (plan 2.4). */
  function isClosedEverywhere(config, dateStr) {
    return closedEntriesFor(config, dateStr).some(function (c) {
      return c.hall === HALL_BOTH && c.applies_to === APPLIES_BOTH && !c.hours;
    });
  }

  function isOrderDay(dateStr, config) {
    var wd = weekdayOf(dateStr);
    if (wd === 0) return false;
    if (wd === 6 && !config.rules.saturday_is_order_day) return false;
    if (isClosedEverywhere(config, dateStr)) return config.rules.closed_day_is_order_day;
    return true;
  }

  /* cutoff_time on the latest order day strictly before the date. */
  function cutoffFor(dateStr, config) {
    for (var k = 1; k <= MAX_DAYS_BACK_FOR_CUTOFF; k++) {
      var day = addDays(dateStr, -k);
      if (isOrderDay(day, config)) {
        var ms = stockholmEpochMs(day, config.rules.cutoff_minutes);
        return {
          orderDay: day,
          epoch: Math.floor(ms / 1000),
          label: day + ' ' + config.rules.cutoff_time
        };
      }
    }
    return null;
  }

  function appliesToMethod(applies, method) {
    return applies === APPLIES_BOTH ||
      (method === PICKUP && applies === APPLIES_PICKUP) ||
      (method === DELIVERY && applies === APPLIES_DELIVERY);
  }

  /*
   * Opening hours for (date, hall, method): weekly hours, then closed-date precedence (plan 2.4). Any matching
   * entry with empty hours closes the day; otherwise the most specific changed-hours entry wins (hall-specific
   * over both halls, then method-specific over both methods; ties go to the first entry).
   * Returns {open, close, entry} or null when closed.
   */
  function hoursFor(dateStr, hall, method, config) {
    var matches = closedEntriesFor(config, dateStr).filter(function (c) {
      return (c.hall === HALL_BOTH || c.hall === hall.name) && appliesToMethod(c.applies_to, method);
    });
    var i;
    for (i = 0; i < matches.length; i++) {
      if (!matches[i].hours) return null;
    }
    if (matches.length) {
      var best = null;
      var bestScore = -1;
      for (i = 0; i < matches.length; i++) {
        var c = matches[i];
        var score = (c.hall !== HALL_BOTH ? 2 : 0) + (c.applies_to !== APPLIES_BOTH ? 1 : 0);
        if (score > bestScore) {
          best = c;
          bestScore = score;
        }
      }
      return { open: best.hours.open, close: best.hours.close, entry: best };
    }
    var weekly = hall.hours[HOUR_KEYS[weekdayOf(dateStr)]];
    return weekly ? { open: weekly.open, close: weekly.close, entry: null } : null;
  }

  /* Pickup slots: from opening, every pickup_slot_minutes, while t <= close - last_slot_before_close. */
  function pickupSlots(dateStr, hall, config) {
    if (!hall || !hall.pickup_enabled) return [];
    if (weekdayOf(dateStr) === 0) return [];
    var h = hoursFor(dateStr, hall, PICKUP, config);
    if (!h) return [];
    var last = h.close - config.rules.pickup_last_slot_before_close_minutes;
    var slots = [];
    for (var t = h.open; t <= last; t += config.rules.pickup_slot_minutes) slots.push(formatHM(t));
    return slots;
  }

  /* Delivery windows: weekday in delivery_weekdays, never Saturday or Sunday, windows inside the delivery hall's hours. */
  function deliveryWindows(dateStr, config) {
    var hall = config.deliveryHall;
    if (!hall) return [];
    var wd = weekdayOf(dateStr);
    if (wd === 0 || wd === 6) return [];
    if (!config.rules.delivery_days[wd]) return [];
    var h = hoursFor(dateStr, hall, DELIVERY, config);
    if (!h) return [];
    return config.rules.delivery_windows.filter(function (w) {
      return w.open >= h.open && w.close <= h.close;
    }).map(formatRange);
  }

  function timesFor(dateStr, method, hall, config) {
    if (method === PICKUP) return pickupSlots(dateStr, hall, config);
    if (method === DELIVERY) return deliveryWindows(dateStr, config);
    return [];
  }

  /*
   * Why a date can or cannot be chosen. reason is null when selectable, else one of:
   * config, paused, method, hall, invalid_date, past, horizon, sunday, saturday_delivery, no_order_day,
   * closed, cutoff_passed.
   */
  function dateStatus(dateStr, method, locationName, nowMs, config) {
    if (!config || !config.ok) return { selectable: false, reason: 'config' };
    if (config.rules.ordering_paused) return { selectable: false, reason: 'paused', message: config.rules.paused_message };
    if (method !== PICKUP && method !== DELIVERY) return { selectable: false, reason: 'method' };
    var hall = resolveHall(config, method, locationName);
    if (!hall) return { selectable: false, reason: 'hall' };
    if (!isDateString(dateStr)) return { selectable: false, reason: 'invalid_date' };

    var today = stockholmParts(nowMs).date;
    var diff = daysBetween(today, dateStr);
    if (diff <= 0) return { selectable: false, reason: 'past' };
    if (diff > config.rules.horizon_days) return { selectable: false, reason: 'horizon' };

    var wd = weekdayOf(dateStr);
    if (wd === 0) return { selectable: false, reason: 'sunday' };
    if (method === DELIVERY && wd === 6) return { selectable: false, reason: 'saturday_delivery' };

    var cutoff = cutoffFor(dateStr, config);
    if (!cutoff) return { selectable: false, reason: 'no_order_day' };

    var times = timesFor(dateStr, method, hall, config);
    if (!times.length) {
      var h = hoursFor(dateStr, hall, method, config);
      var closedEntry = null;
      closedEntriesFor(config, dateStr).forEach(function (c) {
        if (!closedEntry && !c.hours && (c.hall === HALL_BOTH || c.hall === hall.name) && appliesToMethod(c.applies_to, method)) closedEntry = c;
      });
      return {
        selectable: false,
        reason: 'closed',
        cutoff: cutoff,
        message: closedEntry ? closedEntry.message : (h && h.entry ? h.entry.message : '')
      };
    }
    if (Math.floor(nowMs / 1000) >= cutoff.epoch) return { selectable: false, reason: 'cutoff_passed', cutoff: cutoff };
    return { selectable: true, reason: null, cutoff: cutoff, times: times, hall: hall };
  }

  function selectable(dateStr, method, locationName, nowMs, config) {
    return dateStatus(dateStr, method, locationName, nowMs, config).selectable;
  }

  /* Every selectable date for a method (and hall, for pickup) from tomorrow to the horizon. */
  function listDates(method, locationName, nowMs, config) {
    if (!config || !config.ok || config.rules.ordering_paused) return [];
    var today = stockholmParts(nowMs).date;
    var out = [];
    for (var k = 1; k <= config.rules.horizon_days; k++) {
      var d = addDays(today, k);
      var s = dateStatus(d, method, locationName, nowMs, config);
      if (s.selectable) {
        out.push({
          date: d,
          weekday: weekdayName(d),
          labelShort: formatDateShort(d),
          labelLong: formatDateLong(d),
          cutoff: s.cutoff,
          times: s.times
        });
      }
    }
    return out;
  }

  function entryMatches(entry, method, hall) {
    return (entry.hall === HALL_BOTH || entry.hall === hall.name) && appliesToMethod(entry.applies_to, method);
  }

  /* A shallow copy of the config without the closed-date entries of one date (for "offered without them?"). */
  function configWithoutEntriesOn(config, dateStr) {
    var copy = {};
    Object.keys(config).forEach(function (k) { copy[k] = config[k]; });
    var byDate = {};
    Object.keys(config.closedByDate).forEach(function (d) {
      if (d !== dateStr) byDate[d] = config.closedByDate[d];
    });
    copy.closedByDate = byDate;
    return copy;
  }

  /*
   * Closed-date notices (QA 2026-09-17, finding F2). An Active nybroe_closed_date entry takes its date out of the date
   * list, and without this its customer_message never reached the customer.
   *
   * For each date from tomorrow to the horizon that has entries: a notice when, for every order combination in scope
   * that would offer the date if that day had no entries, the entries leave no pickup slot or delivery window, and at
   * least one such combination exists. So a Sunday, or a Saturday for delivery, never gets a notice: those dates were
   * never in the list. Scope: the chosen method and hall. No method means both methods; pickup without a known
   * pickup hall means every pickup hall; delivery always uses the delivery hall.
   *
   * Returns [{date, message}] sorted by date. message is the first non-empty customer_message among the entries that
   * close the date for a combination in scope, else '' (the caller then shows its fallback text).
   */
  function closedDateNotices(nowMs, config, method, locationName) {
    if (!config || !config.ok || config.rules.ordering_paused) return [];
    var m = str(method);
    var combos = [];
    if (m === PICKUP || m === '') {
      var chosen = m === PICKUP ? resolveHall(config, PICKUP, locationName) : null;
      var pickupHalls = chosen ? [chosen] : config.halls.filter(function (h) { return h.pickup_enabled; });
      pickupHalls.forEach(function (h) { combos.push({ method: PICKUP, hall: h }); });
    }
    if ((m === DELIVERY || m === '') && config.deliveryHall) combos.push({ method: DELIVERY, hall: config.deliveryHall });
    if (!combos.length) return [];

    var today = stockholmParts(nowMs).date;
    var out = [];
    Object.keys(config.closedByDate).sort().forEach(function (dateStr) {
      var diff = daysBetween(today, dateStr);
      if (diff <= 0 || diff > config.rules.horizon_days) return;
      var entries = closedEntriesFor(config, dateStr);
      var without = configWithoutEntriesOn(config, dateStr);
      var offered = 0;
      var dropped = 0;
      var message = '';
      combos.forEach(function (c) {
        if (!timesFor(dateStr, c.method, c.hall, without).length) return;
        offered += 1;
        if (timesFor(dateStr, c.method, c.hall, config).length) return;
        dropped += 1;
        var matching = entries.filter(function (e) { return entryMatches(e, c.method, c.hall); });
        /* Whole-day closures first, then changed hours that leave no slot. */
        matching.filter(function (e) { return !e.hours; }).concat(matching.filter(function (e) { return !!e.hours; }))
          .forEach(function (e) {
            if (!message && e.message) message = e.message;
          });
      });
      if (offered > 0 && dropped === offered) out.push({ date: dateStr, message: message });
    });
    return out;
  }

  /* Earliest selectable date for a method; for pickup across every pickup hall. Returns {date, hall} or null. */
  function earliestDate(method, nowMs, config) {
    if (!config || !config.ok || config.rules.ordering_paused) return null;
    var halls = method === PICKUP
      ? config.halls.filter(function (h) { return h.pickup_enabled; })
      : (config.deliveryHall ? [config.deliveryHall] : []);
    var best = null;
    halls.forEach(function (h) {
      var dates = listDates(method, h.location_name, nowMs, config);
      if (dates.length && (!best || dates[0].date < best.date)) best = { date: dates[0].date, hall: h.location_name };
    });
    return best;
  }

  /* ------------------------------------------------------------------ cart attributes */

  function readAttr(attrs, key) {
    return str(attrs ? attrs[key] : '');
  }

  function uniquePush(list, keys) {
    keys.forEach(function (k) {
      if (list.indexOf(k) < 0) list.push(k);
    });
  }

  /*
   * Validate the cart attributes against the rules at time nowMs.
   * Returns {ok, errors[{code, key, params}], clear[keys], derived{Veckodag, Stopptid, StopptidEpoch}}.
   * options.ignoreStamp: skip the StopptidEpoch == cutoff(Datum) check (the checkout guard rewrites the stamp).
   */
  function validate(attrs, nowMs, config, options) {
    var opts = options || {};
    var errors = [];
    var clear = [];
    var derived = null;
    function fail(code, key, clearKeys, params) {
      var e = { code: code };
      if (key) e.key = key;
      if (params) e.params = params;
      errors.push(e);
      if (clearKeys) uniquePush(clear, clearKeys);
    }
    function result() {
      return { ok: errors.length === 0, errors: errors, clear: clear, derived: derived };
    }

    if (!config || !config.ok) {
      fail('config');
      return result();
    }
    if (config.rules.ordering_paused) {
      fail('paused', null, null, { message: config.rules.paused_message });
      return result();
    }

    var method = readAttr(attrs, 'Metod');
    if (method === '') {
      fail('method_missing', 'Metod');
      return result();
    }
    if (method !== PICKUP && method !== DELIVERY) {
      fail('method_invalid', 'Metod', ['Metod', 'Hall', 'Telefon', 'Postnummer', 'Zon'].concat(DATE_KEYS));
      return result();
    }

    var hallName = readAttr(attrs, 'Hall');
    var hall = null;
    if (hallName === '') {
      fail('hall_missing', 'Hall');
    } else {
      hall = resolveHall(config, method, hallName);
      if (!hall) fail('hall_invalid', 'Hall', ['Hall'].concat(DATE_KEYS));
    }

    if (method === PICKUP && config.rules.pickup_phone_required) {
      var phone = readAttr(attrs, 'Telefon');
      if (phone === '') fail('phone_missing', 'Telefon');
      else if (!phoneOk(phone)) fail('phone_invalid', 'Telefon');
    }

    var zoneOk = true;
    if (method === DELIVERY) {
      var pcRaw = readAttr(attrs, 'Postnummer');
      if (pcRaw === '') {
        zoneOk = false;
        fail('postcode_missing', 'Postnummer');
      } else {
        var zone = zoneFor(pcRaw, config);
        if (!zone) {
          zoneOk = false;
          fail('postcode_not_served', 'Postnummer', ['Postnummer', 'Zon'].concat(DATE_KEYS), { postcode: pcRaw });
        } else if (readAttr(attrs, 'Zon') !== zone.label) {
          fail('zone_mismatch', 'Zon', ['Zon'], { expected: zone.label });
        }
      }
    }

    if (!hall || !zoneOk) return result();

    var dateStr = readAttr(attrs, 'Datum');
    if (dateStr === '') {
      fail('date_missing', 'Datum');
      return result();
    }
    var status = dateStatus(dateStr, method, hall.location_name, nowMs, config);
    if (!status.selectable) {
      fail('date_' + status.reason, 'Datum', DATE_KEYS, {
        date: dateStr,
        cutoff: status.cutoff || null,
        message: status.message || ''
      });
      return result();
    }

    derived = {
      Veckodag: weekdayName(dateStr),
      Stopptid: status.cutoff.label,
      StopptidEpoch: String(status.cutoff.epoch)
    };

    if (!opts.ignoreStamp) {
      var stamp = readAttr(attrs, 'StopptidEpoch');
      if (stamp !== derived.StopptidEpoch) {
        fail(stamp === '' ? 'stamp_missing' : 'stamp_mismatch', 'StopptidEpoch', ['Stopptid', 'StopptidEpoch'],
          { expected: derived.StopptidEpoch });
      }
    }

    var time = readAttr(attrs, 'Tid');
    if (time === '') fail('time_missing', 'Tid');
    else if (status.times.indexOf(time) < 0) fail('time_invalid', 'Tid', ['Tid'], { time: time });

    return result();
  }

  /* Attributes the checkout guard writes after a valid click (plan 3.1 and 3.4). */
  function checkoutStamp(attrs, now, config) {
    var dateStr = readAttr(attrs, 'Datum');
    var cutoff = isDateString(dateStr) && config && config.ok ? cutoffFor(dateStr, config) : null;
    return {
      Veckodag: isDateString(dateStr) ? weekdayName(dateStr) : '',
      Stopptid: cutoff ? cutoff.label : '',
      StopptidEpoch: cutoff ? String(cutoff.epoch) : '',
      Validerad: formatStockholmIso(now.ms),
      Tidskalla: now.source,
      Regelversion: config && config.rules ? config.rules.rules_version : ''
    };
  }

  /*
   * Empty-cart rule (plan 3.1, C15): cart/clear keeps attributes, so when the cart has 0 items every key of the
   * contract is written back as "". Returns the attributes object to send, or null when nothing needs clearing.
   * The cart note is never touched.
   */
  function emptyCartClear(cart) {
    if (!cart || cart.item_count !== 0) return null;
    var attrs = cart.attributes || {};
    var anySet = ATTRIBUTE_KEYS.some(function (k) {
      return str(attrs[k]) !== '';
    });
    if (!anySet) return null;
    var out = {};
    ATTRIBUTE_KEYS.forEach(function (k) {
      out[k] = '';
    });
    return out;
  }

  /* Facts for the rule sentence in plan 3.3 step 3 (the copy itself lives in the locale files). */
  function ruleFacts(config) {
    if (!config || !config.ok) return null;
    var r = config.rules;
    return {
      cutoffTime: r.cutoff_time,
      mondayOrderWeekday: r.saturday_is_order_day ? 'lördag' : 'fredag',
      saturdayPickupOnly: true,
      sundayClosed: true,
      deliveryWeekdays: r.delivery_weekdays.slice(),
      insidePrice: config.zones.inside.price,
      outsidePrice: config.zones.outside.price,
      contactEmail: r.contact_email,
      contactPhone: r.contact_phone
    };
  }

  var NybRules = {
    VERSION: '1.0.0',
    TIME_ZONE: TZ,
    PICKUP: PICKUP,
    DELIVERY: DELIVERY,
    HALL_BOTH: HALL_BOTH,
    APPLIES_BOTH: APPLIES_BOTH,
    APPLIES_PICKUP: APPLIES_PICKUP,
    APPLIES_DELIVERY: APPLIES_DELIVERY,
    ZONE_INSIDE_LABEL: ZONE_INSIDE_LABEL,
    ZONE_OUTSIDE_LABEL: ZONE_OUTSIDE_LABEL,
    ATTRIBUTE_KEYS: ATTRIBUTE_KEYS.slice(),
    DATE_KEYS: DATE_KEYS.slice(),

    parseHM: parseHM,
    parseRange: parseRange,
    formatHM: formatHM,
    isDateString: isDateString,
    addDays: addDays,
    daysBetween: daysBetween,
    weekdayOf: weekdayOf,
    weekdayName: weekdayName,
    formatDateShort: formatDateShort,
    formatDateLong: formatDateLong,
    formatDayMonth: formatDayMonth,

    stockholmParts: stockholmParts,
    stockholmOffsetMinutes: stockholmOffsetMinutes,
    stockholmEpochMs: stockholmEpochMs,
    formatStockholmIso: formatStockholmIso,
    resolveNow: resolveNow,

    parseZoneString: parseZoneString,
    normalizePostcode: normalizePostcode,
    zoneFor: zoneFor,
    phoneOk: phoneOk,

    configFromSectionsResponse: configFromSectionsResponse,
    normalizeConfig: normalizeConfig,
    loadConfigFromSectionsResponse: loadConfigFromSectionsResponse,

    resolveHall: resolveHall,
    isOrderDay: isOrderDay,
    cutoffFor: cutoffFor,
    hoursFor: hoursFor,
    pickupSlots: pickupSlots,
    deliveryWindows: deliveryWindows,
    timesFor: timesFor,
    dateStatus: dateStatus,
    selectable: selectable,
    listDates: listDates,
    earliestDate: earliestDate,
    closedDateNotices: closedDateNotices,

    validate: validate,
    checkoutStamp: checkoutStamp,
    emptyCartClear: emptyCartClear,
    ruleFacts: ruleFacts
  };

  if (typeof window !== 'undefined') {
    window.NybRules = NybRules;
  }
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = NybRules;
  }
})();
